import React, { useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Box,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import TeacherQuestionView from "./Questions/TeacherQuestionView";
import {
  useAssignment,
  useSetAssignment,
} from "../../globals/AssignmentContext";
import { updateAssignmentAction } from "../../actions/updateAssignment";
import { updateAssignmentQuestionsAction } from "../../actions/updateAssignmentQuestions";
import { updateQuestionAction } from "../../actions/updateQuestion";
import axios from "axios";
import ClassPicker from "./ClassPicker";
import GenericModal from "../Modals/GenericModal/GenericModal";
import { addAssignmentAction } from "../../actions/addAssignment";
import { useSetClass } from "../../globals/ClassContext";
import { removeAssignmentAction } from "../../actions/removeAssignment";
import { fetchAssignmentAction } from "../../actions/fetchAssignment";

export default function TeacherAssignmentView() {
  const { REACT_APP_DEBUGGER_SERVER_URL } = process.env;
  const assignment = useAssignment();
  const setAssignment = useSetAssignment();
  const setClass = useSetClass();
  // Used for adding assignment to class modal (ClassPicker)
  const [open, setOpen] = React.useState(false);

  // Holds data in the forms, is updated on any input being changed
  const [assignmentDetails, setAssignmentDetails] = React.useState(assignment);
  // Holds error states for inputs in the form, is updated on submission and if any errors become true submission is blocked
  // And error text will be shows for the inputs that were marked as having errors
  const [error, setError] = React.useState({
    name: false,
    open_date: false,
    due_date: false,
  });

  // Runs on the update details button being pressed and on questions being added, removed, or saved
  const handleSubmit = async () => {
    let hasErrors = false;

    // Ensuring the assignment has a name
    if (assignmentDetails.name.length === 0) {
      setError({ ...error, ["name"]: true });
      hasErrors = true;
    } else setError({ ...error, ["name"]: false });

    // Fetching the questions that this assignment has
    const requests = assignmentDetails?.questions?.map((question) => {
      let questionID = typeof question === "string" ? question : question._id;
      return axios.get(
        `${REACT_APP_DEBUGGER_SERVER_URL}/question/${questionID}`
      );
    });
    const responses = await axios.all(requests);
    // So that we can get the total points for the assignment
    let pointTotal = 0;
    responses.forEach((response) => {
      pointTotal += response.data.points;
    });

    // Set the updated point total
    assignmentDetails["points"] = pointTotal;

    // Update the assignment if there were no form errors
    if (!hasErrors)
      updateAssignmentAction(assignment?._id, assignmentDetails, setAssignment);
  };

  // Check whether or not the teacher should be able to edit questions.
  // We do not want them editing question details for an assignment that is open to a class as there would be a myriad of bugs with in progress and submitted attempts
  const hasBeenAssigned =
    dayjs(assignment?.open_date) < dayjs() && assignment?.assigned_to?.length > 0;

  // There is almost certainly a better way to create the question accordions but this works and time is up
  let questionAccordions = assignment?.questions?.map((questionID, i) => (
    <TeacherQuestionView
      key={typeof questionID === "string" ? questionID : questionID?._id}
      questionID={typeof questionID === "string" ? questionID : questionID?._id}
      questionNumber={i + 1}
      assignment={assignment}
      saveAssignment={handleSubmit}
      disabled={hasBeenAssigned}
    ></TeacherQuestionView>
  ));
  useEffect(() => {
    // Whenever the assignment has changed or been updated, update the assignment details object and repopulate the questions
    questionAccordions = assignment?.questions?.map((questionID, i) => (
      <TeacherQuestionView
        key={typeof questionID === "string" ? questionID : questionID?._id}
        questionID={
          typeof questionID === "string" ? questionID : questionID?._id
        }
        questionNumber={i + 1}
        assignment={assignment}
        saveAssignment={handleSubmit}
        disabled={hasBeenAssigned}
      ></TeacherQuestionView>
    ));
    setAssignmentDetails(assignment);
  }, [assignment]);

  if (!assignment || Object.keys(assignment).length === 0) {
    return <></>;
  }

  // Runs when add question button is clicked, creates a question object in the database
  // appends it to the current array of question ids and then updates the assignment's questions
  // We also then submit so that the point total is recalculated with the new question already in the database
  const handleAddQuestion = async () => {
    const question = await updateQuestionAction({
      name: "Question",
      points: 0,
    });
    let questions = assignment.questions;
    questions.push(question);
    await updateAssignmentQuestionsAction(
      assignment._id,
      questions,
      setAssignment
    ).then(() => {
      handleSubmit();
    });
  };

  // Runs when a class is chosen in the ClassPicker component to add to or remove this assignment from a class
  const handleClassPicked = async (chosenClassID, add) => {
    if (add) {
      await addAssignmentAction(chosenClassID, assignment._id, setClass).then(
        () => {
          fetchAssignmentAction(assignment._id, setAssignment);
        }
      );
    } else {
      await removeAssignmentAction(
        chosenClassID,
        assignment._id,
        setClass
      ).then(() => {
        fetchAssignmentAction(assignment._id, setAssignment);
      });
    }
  };

  // Ensuring that data is not bad
  assignment.open_date =
    typeof assignment.open_date === "undefined" || assignment.open_date === null
      ? dayjs(Date.now())
      : dayjs(assignment.open_date);
  assignment.due_date =
    typeof assignment.due_date === "undefined" || assignment.due_date === null
      ? dayjs(Date.now())
      : dayjs(assignment.due_date);
  assignment.name =
    typeof assignment.name === "undefined" || assignment.name === null
      ? ""
      : assignment.name;

  // Run when changing details (dynamically done) or dates (not dynamic since the datepicker package returns events differently)
  const updateDetail = (event) => {
    setAssignmentDetails({
      ...assignmentDetails,
      [event.target.name]: event.target.value,
    });
  };

  const updateOpenDate = (date) => {
    setAssignmentDetails({ ...assignmentDetails, ["open_date"]: date });
  };

  const updateDueDate = (date) => {
    setAssignmentDetails({ ...assignmentDetails, ["due_date"]: date });
  };

  return (
    assignment && assignmentDetails && (
      <Container
      key={assignment?._id}
      component="main"
      sx={{ paddingTop: "4px" }}
    >
      <Paper elevation={3}>
        <Container sx={{ paddingLeft: "20px", paddingTop: "20px" }}>
          <Grid
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              flex: "auto",
            }}
          >
            <Typography
              variant="h4"
              sx={{ margin: "5px 0px", fontWeight: "bold" }}
            >
              {assignment.name}
            </Typography>
          </Grid>
          {/* Adding assignment to a class button and modal */}
          <Button
            sx={{ margin: "15px 5px" }}
            onClick={() => setOpen(true)}
            variant="outlined"
            color="primary"
            size="small"
          >
            Assign to Classes
          </Button>
          <GenericModal
            open={open}
            handleClose={() => setOpen(false)}
            body={
              <ClassPicker
                open={open}
                handleClassPicked={handleClassPicked}
                sx={{
                  color: "red",
                  transform: "translate(-50%, -50%)",
                  top: "50%",
                }}
              />
            }
          />
        </Container>
        <Grid
          sx={{
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            justifyContent: "space-between",
            flex: "auto",
          }}
        >
          <Typography variant="h5" component="h2" xs={{ padding: "20px" }}>
            Assignment Details
          </Typography>
          <Grid
            sx={{
              paddingLeft: "20px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              flex: "auto",
            }}
          >
            <TextField
              error={error.name}
              helperText={error.name ? "Please input a name" : ""}
              value={assignmentDetails.name}
              onChange={updateDetail}
              sx={{ fontSize: "30px" }}
              margin="normal"
              name="name"
              id="assignment-name"
              label="Assignment Name"
              variant="standard"
            />

            <TextField
              disabled
              value={assignmentDetails.points}
              type="number"
              margin="normal"
              sx={{ width: "100px", marginLeft: "20px" }}
              name="points"
              id={"assignment-points"}
              label="Total Points"
            />
          </Grid>
          <Grid
            sx={{
              paddingLeft: "20px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              flex: "auto",
            }}
          >
            <Box>
              <DateTimePicker
                label="Open Date"
                name="open_date"
                value={assignmentDetails.open_date}
                onChange={updateOpenDate}
              />
            </Box>
            <Box sx={{ padding: "20px" }}>
              <DateTimePicker
                label="Close Date"
                name="due_date"
                value={assignmentDetails.due_date}
                onChange={updateDueDate}
              />
            </Box>
          </Grid>
          <Button
            sx={{ margin: "15px 5px" }}
            onClick={handleSubmit}
            variant="outlined"
            color="primary"
            size="small"
          >
            Update Assignment Details
          </Button>
          {hasBeenAssigned ? (
            <>
              <Typography
                variant="subtitle1"
                color="secondary"
                sx={{ padding: "20px" }}
              >
                Question editing has been disabled as this assignment is open to
                students.
                <br />
                You may only edit questions on assignments which are not open or
                have not been assigned to a class.
              </Typography>
              <Typography
                variant="subtitle2"
                color="secondary"
                sx={{ padding: "20px" }}
              >
                If you need to change this assignment's questions and students
                have already begun submitting attempts, then we recommend
                creating a new copy of this assignment which you can edit,
                assign to the class, and ask them to complete instead. In that
                case this old copy may be unassigned and or deleted.
                <br />
                If you attempt to unassign this assignment to make changes and
                subsequently reassign it to the same class, then submissions to
                the old questions may remain and can cause grading errors.
              </Typography>
            </>
          ) : (
            <></>
          )}
          <Typography
            variant="h5"
            component="h2"
            color={hasBeenAssigned ? "primary.light" : "primary"}
          >
            Questions
          </Typography>
          {questionAccordions}
          <Button
            disabled={hasBeenAssigned}
            sx={{ margin: "15px 5px" }}
            onClick={handleAddQuestion}
            variant="outlined"
            color="primary"
            size="small"
          >
            Add A Question
          </Button>
        </Grid>
      </Paper>
    </Container>
    )
  );
}
